import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import ThreadPostCard from '../CommunityThread/ThreadPostCard';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource, DATA_SOURCE_MOCK } from '../../utils/communityDataSource';

/** Admin detail: Threads-style post + expanded replies; side panel for votes/likes. */
export default function ThreadDetailModal({ threadId, onClose, actingUserId = 1001 }) {
  const dataSource = useCommunityDataSource();
  const isMock = dataSource === DATA_SOURCE_MOCK;

  const threadQuery = useQuery({
    queryKey: ['communityThread', dataSource, threadId],
    queryFn: () => provider.getThreadById(threadId, { acting_user_id: actingUserId }),
    enabled: !!threadId,
  });

  // The community API has no "list likes" endpoint, so this is mock-only.
  const likesQuery = useQuery({
    queryKey: ['communityLikes', dataSource, threadId],
    queryFn: () => provider.getLikesForThread(threadId),
    enabled: !!threadId && isMock,
  });

  const t = threadQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-dark-card border border-dark-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border shrink-0">
          <h2 className="text-lg font-semibold text-dark-text">Thread</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-text-secondary hover:text-primary"
          >
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {threadQuery.isLoading && (
            <p className="p-6 text-dark-text-secondary text-sm">Loading…</p>
          )}
          {threadQuery.error && (
            <p className="p-6 text-red-400 text-sm">{threadQuery.error.message}</p>
          )}
          {t && (
            <ThreadPostCard
              thread={t}
              actingUserId={actingUserId}
              defaultExpanded
              onAdminDetails={null}
            />
          )}

          {t && (
            <div className="px-5 pb-4 border-t border-dark-border">
              <p className="text-xs text-dark-text-muted uppercase tracking-wide mt-3 mb-2">
                Votes
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-dark-surface border border-dark-border">
                  ▲ up {t.up_count ?? 0}
                </span>
                <span className="px-2 py-1 rounded-full bg-dark-surface border border-dark-border">
                  ▼ down {t.down_count ?? 0}
                </span>
                {t.my_vote != null && (
                  <span className="px-2 py-1 rounded-full bg-primary/15 border border-primary/40 text-primary">
                    my vote: {t.my_vote === 1 ? 'up' : t.my_vote === -1 ? 'down' : 'none'}
                  </span>
                )}
                {t.board?.name && (
                  <span className="px-2 py-1 rounded-full bg-dark-surface border border-dark-border">
                    board: {t.board.name}
                  </span>
                )}
                {Array.isArray(t.tags) && t.tags.length > 0 && (
                  <span className="px-2 py-1 rounded-full bg-dark-surface border border-dark-border">
                    tags: {t.tags.join(', ')}
                  </span>
                )}
              </div>
            </div>
          )}

          {likesQuery.data?.length > 0 && (
            <div className="px-5 pb-4 border-t border-dark-border">
              <p className="text-xs text-dark-text-muted uppercase tracking-wide mt-3 mb-2">
                Liked by
              </p>
              <div className="flex flex-wrap gap-2">
                {likesQuery.data.map((l) => (
                  <span
                    key={`${l.thread_id}-${l.user_id}`}
                    className="text-xs px-2 py-1 rounded-full bg-dark-surface border border-dark-border"
                  >
                    user_{l.user_id}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
